import { Typography } from "@material-ui/core"
import { HomePageCarStrip } from "api"
import NextLink from "common/bit/NextLink"
import { buildShopLink } from "common/buildShopLink"
import { setSelectedColor } from "components/shopFeatures/color/colorSlice"
import { motion } from "framer-motion"
import MoreDetailsButton from "reusable/MoreDetailsButton"
import { useAppDispatch, useTypedSelector } from "store"
import styled, { css } from "styled-components"
import { TypographyCenter } from "theme/shared"

const StyledFooterDiv = styled.div<{ $isShop: boolean }>`
  min-height: 190px;
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
  margin-top: 20px;
  ${(p) =>
    p.$isShop &&
    css`
      margin-top: 0;
    `}
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 20px;
    height: 190px;
    margin-top: 0px;
    ${(p) =>
      p.$isShop &&
      css`
        height: auto;
        min-height: 0;
      `}
  }
`
const StyledPriceTitle = styled(TypographyCenter).attrs({ variant: "body1" })<{
  isShop?: boolean
}>`
  font-size: 14px;
  color: ${(p) => p.theme.colors.onyx};
  ${(p) =>
    p.isShop &&
    css`
      font-size: 20px;
      font-weight: 400;
    `}
`

const StyledPrice = styled(Typography).attrs({ variant: "body1" })`
  font-family: MazdaType, OENaPro, sans-serif;
  font-size: 24px;
  text-align: center;
  letter-spacing: 0.08em;
  font-weight: 400;
`

const StyledPriceWrapper = styled.div<{ $justify: string }>`
  width: 100%;
  min-width: 270px;
  justify-content: ${(p) => p.$justify};
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 16px;
  }
`

const StyledPriceLine = styled.div`
  width: 1px;
  height: 62px;
  background: ${(p) => p.theme.colors.nonExistentLightGray};
  transform: rotate(29.19deg);
`

const StyledLicenseFee = styled(Typography).attrs({ variant: "body2" })`
  font-size: 14px;
  color: ${(p) => p.theme.colors.black};
  text-align: center;
  margin: 0 auto;
`

const StyledPurchase = styled(Typography).attrs({ variant: "body2" })`
  text-align: center;
  margin-top: 4px;
  height: 23px;
  color: ${(p) => p.theme.colors.mazdaRed};
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 20px;
  }
`

const StyledDetailsDiv = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: fit-content;
  margin: 0 auto;
`
const StyledDetailsText = styled(Typography).attrs({ variant: "body2" })`
  margin-left: 10px;
`
const StyledShopText = styled(StyledDetailsText)`
  font-size: 20px;
`
const StyledDiv = styled.div<{
  $marginTop?: number
  $marginBottom?: number
  isShop?: boolean
}>`
  ${(p) => p.$marginTop && `margin-top: ${p.$marginTop}px;`}
  ${(p) => p.$marginBottom && `margin-bottom: ${p.$marginBottom}px;`}
  ${(p) =>
    p.isShop &&
    css`
      display: flex;
      align-items: baseline;
    `}
`

type SwiperFooterText = {
  card: HomePageCarStrip
  activeIndex: number
  link: string
  colorIndex: number
  isShop?: boolean
}

const SwiperFooterText = ({
  card,
  activeIndex,
  link,
  colorIndex,
  isShop,
}: SwiperFooterText) => {
  const dispatch = useAppDispatch()
  const carLink = link

  const detailData = useTypedSelector((state) => state?.detailing)
  const { detailing } = detailData

  const { id: carId, modelColors } = card
  const color = modelColors[colorIndex]?.colorCode
  const shopUrl = buildShopLink(detailing, color, carId)

  const saveSelectedColor = () => {
    dispatch(setSelectedColor(card.modelColors[activeIndex].colorCode))
  }

  return (
    <StyledFooterDiv $isShop={isShop}>
      {card.carState !== "Future" && (
        <StyledPriceWrapper
          $justify={
            card?.mountlyPayment && !isShop ? "space-between" : "center"
          }
        >
          <StyledDiv isShop={isShop}>
            <StyledPriceTitle isShop={isShop}>
              {isShop ? "מ-" : "החל מ-"}
            </StyledPriceTitle>
            <StyledPrice>₪{card.price.toLocaleString()}</StyledPrice>
          </StyledDiv>
          {!!card?.mountlyPayment && !isShop && (
            <>
              <div>
                <StyledPriceLine />
              </div>
              <StyledDiv>
                <StyledPriceTitle>החזר חודשי החל מ-</StyledPriceTitle>
                <StyledPrice>
                  ₪
                  {card.mountlyPayment
                    ? card.mountlyPayment.toLocaleString()
                    : 0}
                </StyledPrice>
              </StyledDiv>
            </>
          )}
        </StyledPriceWrapper>
      )}
      <StyledDiv $marginBottom={10}>
        {card?.licenceFee > 0 && card.carState !== "Future" && (
          <StyledLicenseFee>
            + אגרת רישוי של ₪{card.licenceFee.toLocaleString()} כולל מע״מ
          </StyledLicenseFee>
        )}
        {card.isInShop && !isShop && (
          <NextLink href={shopUrl}>
            <StyledPurchase>ניתן להזמנה מיידית אונליין</StyledPurchase>
          </NextLink>
        )}
      </StyledDiv>
      {card.isInShop || !isShop ? (
        <NextLink
          href={carLink}
          onClick={saveSelectedColor}
          aria-label={`Find out more about ${card.name}. גלו עוד על  ${card.name} `}
        >
          <StyledDetailsDiv layout>
            {!isShop ? (
              <StyledDetailsText>גלו עוד</StyledDetailsText>
            ) : (
              <StyledShopText>לתחילת תהליך רכישה אונליין</StyledShopText>
            )}
            <MoreDetailsButton />
          </StyledDetailsDiv>
        </NextLink>
      ) : (
        <NextLink
          href="/contact-us"
          onClick={saveSelectedColor}
          aria-label={`Find out more about ${card.name}. גלו עוד על  ${card.name} `}
        >
          <StyledDetailsDiv layout>
            <StyledShopText>לבדיקת זמינות באולם</StyledShopText>

            <MoreDetailsButton />
          </StyledDetailsDiv>
        </NextLink>
      )}
    </StyledFooterDiv>
  )
}

export default SwiperFooterText
